import { useMediaQuery } from '@mui/material';
import moment from 'moment-timezone';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { SingleMonthDatePicker } from 'components/MultiProductDateForm/SingleMonthDatePicker';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { ReduxState } from 'ducks';
import { fetchProductInstances } from 'ducks/client/productInstances';
import { getAttrByDateMap, getPriceByDateMap } from 'lib/util/datePickerHelpers';
import { ProductSummary } from 'models/product';

interface OwnProps {
  product?: ProductSummary;
  onDateClick: (date: string) => void;
}

/* eslint-disable no-use-before-define */
type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & OwnProps;
/* eslint-enable no-use-before-define */

export const MultiProductDatePickerComponent = ({
  fetchProductInstancesForDateRange,
  loading,
  onDateClick,
  productInstances,
  product,
}: Props) => {
  // Currently visible calendar month as 'YYYY-MM-DD' string
  const [month, setMonth] = React.useState<string>(moment().format('YYYY-MM-DD'));

  const { t } = useTranslation();

  const isSmallScreen = useMediaQuery('(max-width: 600px)');
  const showMonthsCount = isSmallScreen ? 1 : 2;

  const { i18n } = useTranslation();
  const { apiKey } = React.useContext(ApiKeyContext);

  const locale = i18n.language;

  const productId = (product && product.id) || '';
  React.useEffect(() => {
    if (!productId) {
      return;
    }

    const firstDay = moment(month).date(1);
    let lastDay = moment(month).endOf('month').add(1, 'day');

    if (showMonthsCount === 2) {
      lastDay = lastDay.endOf('month').add(1, 'day');
    }

    fetchProductInstancesForDateRange(
      apiKey,
      productId,
      firstDay.format('YYYY-MM-DD'),
      lastDay.format('YYYY-MM-DD'),
      locale
    );
  }, [apiKey, fetchProductInstancesForDateRange, locale, month, productId, showMonthsCount]);

  const handleNextMonthClick = () => {
    let newMonth = moment(month).endOf('month').add(1, 'day');
    if (showMonthsCount === 2) {
      newMonth = newMonth.endOf('month').add(1, 'day');
    }
    setMonth(newMonth.format('YYYY-MM-DD'));
  };
  const handlePrevMonthClick = () => {
    let newMonth = moment(month).startOf('month').subtract(1, 'day');
    if (showMonthsCount === 2) {
      newMonth = newMonth.startOf('month').subtract(1, 'day');
    }
    setMonth(newMonth.format('YYYY-MM-DD'));
  };

  const timezone = (product && product.start_timezone) || '';
  const attrByDateMap = getAttrByDateMap(productInstances, timezone, 0);
  const priceByDateMap = getPriceByDateMap(productInstances, timezone);

  let showBookingAllowedMessage = false;
  const agentGuestBookingAllowedDaysBefore =
    product?.agent_guest_booking_period_settings?.booking_allowed_days_before_participation;
  if (agentGuestBookingAllowedDaysBefore) {
    const bookingRestrictedDate = moment()
      .tz(product?.start_timezone ?? '')
      .add(agentGuestBookingAllowedDaysBefore + 1, 'days')
      .format('YYYY-MM-DD');

    const lastDayOfSelectedMonths =
      showMonthsCount === 2
        ? moment(month).endOf('month').add(1, 'day').endOf('month').format('YYYY-MM-DD')
        : moment(month).endOf('month').format('YYYY-MM-DD');

    if (bookingRestrictedDate <= lastDayOfSelectedMonths) {
      showBookingAllowedMessage = true;
    }
  }

  return (
    <>
      <div className="widget-multi-calendar__body__main">
        <SingleMonthDatePicker
          month={month}
          loading={loading}
          attrByDateMap={attrByDateMap}
          priceByDateMap={priceByDateMap}
          onPrevMonthClick={handlePrevMonthClick}
          onNextMonthClick={showMonthsCount === 1 ? handleNextMonthClick : undefined}
          onDateClick={onDateClick}
        />
        {showMonthsCount === 2 && (
          <SingleMonthDatePicker
            loading={loading}
            month={moment(month).endOf('month').add(1, 'day').format('YYYY-MM-DD')}
            attrByDateMap={attrByDateMap}
            priceByDateMap={priceByDateMap}
            onNextMonthClick={handleNextMonthClick}
            onDateClick={onDateClick}
          />
        )}
      </div>

      <div className="widget-multi-calendar__booking__allowed__text">
        {showBookingAllowedMessage &&
          t('Reservations will start {{days}} days before participation', {
            days: agentGuestBookingAllowedDaysBefore ?? 0,
          })}
      </div>
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  loading: state.productInstances.loading,
  productInstances: state.productInstances.all,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, any>) => ({
  fetchProductInstancesForDateRange: (
    apiKey: string,
    productId: string,
    startDate: string,
    endDate: string,
    language: string
  ) => dispatch(fetchProductInstances(apiKey, productId, startDate, endDate, language)),
});

export const MultiProductDatePicker = compose<React.ComponentType<OwnProps>>(
  connect(mapStateToProps, mapDispatchToProps)
)(MultiProductDatePickerComponent);
